/**
 * InvoiceForm Types
 * 
 * Type definitions for the invoice form component.
 */
import { Customer, InvoiceLine, Product } from 'types';
import { FormLineItem } from 'app/hooks';

/**
 * Type for form data
 */
export interface InvoiceFormData {
  customer: Customer | null;
  date: Date | null;
  deadline: Date | null;
  lineItems: FormLineItem[];
}

/**
 * Type for validation errors
 */
export interface ValidationErrors {
  customer?: string;
  lineItems?: string;
}

/**
 * Type for original invoice data (used for edit mode)
 */
export interface OriginalInvoice {
  id: number;
  customer_id: number | null;
  finalized: boolean;
  invoice_lines: InvoiceLine[];
}

/**
 * Location state interface for cloning
 * Note: We no longer pass invoice data via location state for editing
 * to avoid stale data issues. The form always fetches fresh data from API.
 */
export interface InvoiceFormLocationState {
  cloneFrom?: {
    customer_id: number | null;
    customer?: Customer | null;
    date: string | null;
    deadline: string | null;
    invoice_lines: Array<{
      id?: number;
      product?: Product | null;
      quantity: number;
      label?: string;
    }>;
  };
}

/**
 * Format date for API (YYYY-MM-DD)
 * Uses local date components to avoid timezone issues.
 * (toISOString() converts to UTC which can shift the date by a day)
 */
export const formatDateForApi = (date: Date | null): string | null => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Validate form and return errors object
 */
export const validateInvoiceForm = (formData: InvoiceFormData): ValidationErrors => {
  const errors: ValidationErrors = {};
  
  if (!formData.customer) {
    errors.customer = 'Please select a customer';
  }

  const activeLines = formData.lineItems.filter(item => item.product);
  if (activeLines.length === 0) {
    errors.lineItems = 'Please add at least one line item with a product';
  }

  return errors;
};
